import { bffRequest } from '../bff/client'
import { BffError } from '../bff/errors'
import type {
  AuditEvent, Biller, BillerSlices, Envelope, FieldExposureResponse, FieldExposureRule, Gateway, GatewayBeneficiaries, GatewayEntry, GrantFieldExposureInput, OrganisationKind, PortalUser, Profile, SupportRequestInput, TransactionsResponse, UsersResponse,
} from './types'

/**
 * biller-api calls, all reached through the BFF's route allowlist at
 * `/bff/api/v1/…`. Anything not on that list is a 404 from the BFF, so the
 * surface here is intentionally closed rather than a generic `get(path)`.
 *
 * No biller identifier appears anywhere in this file. That is the point: the
 * BFF sets `X-Biller-Id` from server-side session state and discards whatever
 * the browser sent, so a client-side call site has no way to assert a tenant —
 * which is what makes the isolation boundary hold even if this code is wrong.
 */

const API = '/bff/api'

/** Unwraps the envelope. `data` is the payload; the error arm never reaches
 *  here because a non-2xx already threw a BffError. */
const unwrap = async <T>(promise: Promise<Envelope<T>>): Promise<T> => (await promise).data

export interface TransactionQuery {
  readonly date_from: string
  readonly date_to: string
  readonly status: readonly string[]
  readonly amount_min: string
  readonly amount_max: string
  readonly ptn: string
  /** Gateway ids to narrow to. Empty means "everything in scope" — the server
   *  intersects this with what the caller is entitled to, so it can only ever
   *  narrow. */
  readonly gateway: readonly string[]
  /** A biller code to narrow to. Resolved to that biller's gateways
   *  server-side and intersected the same way — it selects nothing, so the
   *  isolation note above still holds: this is a filter, not a tenant. */
  readonly biller: string
  readonly page: number
  readonly page_size: number
  readonly sort_by: 'date' | 'amount'
  readonly sort_dir: 'asc' | 'desc'
  /** Internal users only (FR-42). */
  readonly beneficiary?: string
  /** MPAY-33571. Refused by the server where service type is withheld (FR-38). */
  readonly service_type?: string
  readonly service_label?: string
  /** MPAY-33568. Open to every requester (AB-39). */
  readonly provider_txid?: string
}

/** Omits empty values rather than sending `?status=&amount_min=` — the API
 *  treats an empty string as absent, but a URL full of empty params makes the
 *  query key noisy and the server logs unreadable. */
export function toQueryString(q: TransactionQuery): string {
  const params = new URLSearchParams()
  const put = (key: string, value: string) => {
    if (value !== '') params.set(key, value)
  }
  put('date_from', q.date_from)
  put('date_to', q.date_to)
  put('status', q.status.join(','))
  put('amount_min', q.amount_min)
  put('amount_max', q.amount_max)
  put('ptn', q.ptn)
  put('provider_txid', q.provider_txid ?? '')
  put('gateway', q.gateway.join(','))
  put('biller', q.biller)
  put('beneficiary', q.beneficiary ?? '')
  put('service_type', q.service_type ?? '')
  put('service_label', q.service_label ?? '')
  params.set('page', String(q.page))
  params.set('page_size', String(q.page_size))
  params.set('sort_by', q.sort_by)
  params.set('sort_dir', q.sort_dir)
  return params.toString()
}

export const getTransactions = (q: TransactionQuery, signal?: AbortSignal): Promise<TransactionsResponse> =>
  unwrap(
    bffRequest<Envelope<TransactionsResponse>>(
      `${API}/v1/transactions?${toQueryString(q)}`,
      signal ? { signal } : {},
    ),
  )

export const getProfile = (signal?: AbortSignal): Promise<Profile> =>
  unwrap(bffRequest<Envelope<Profile>>(`${API}/v1/profile`, signal ? { signal } : {}))

export const getBillers = async (signal?: AbortSignal): Promise<readonly Biller[]> =>
  (await unwrap(bffRequest<Envelope<{ billers: Biller[] }>>(`${API}/v1/billers`, signal ? { signal } : {}))).billers

/**
 * The gateway catalogue.
 *
 * Internal users get every gateway in the warehouse and may filter it; biller
 * users get only their own, and the server ignores the filters that would
 * widen that. The same call therefore backs both the mapping screen and the
 * gateway dropdown on the transactions filter bar.
 */
export const getGateways = async (
  filter: GatewayQuery = {},
  signal?: AbortSignal,
): Promise<readonly GatewayEntry[]> => {
  const params = new URLSearchParams()
  if (filter.q) params.set('q', filter.q)
  if (filter.unmapped) params.set('unmapped', 'true')
  if (filter.biller) params.set('biller', filter.biller)
  const query = params.toString()
  return (
    await unwrap(
      bffRequest<Envelope<{ gateways: GatewayEntry[] }>>(
        `${API}/v1/gateways${query ? `?${query}` : ''}`,
        signal ? { signal } : {},
      ),
    )
  ).gateways
}

export interface GatewayQuery {
  readonly q?: string
  readonly unmapped?: boolean
  readonly biller?: string
}

export interface CreateBillerInput {
  readonly biller_id: string
  readonly name: string
  readonly first_user_email: string
  readonly first_name: string
  readonly last_name: string
  /** Optional: assignment happens on the Gateways screen; the API treats an
   *  absent field as "none yet". */
  readonly gateways?: readonly string[]
  /** "biller" (default) or "beneficiary"; fixed at creation (FR-40). */
  readonly kind?: OrganisationKind
  /** "HH:MM" or empty (FR-41). */
  readonly settlement_day_start?: string
}

export interface UpdateBillerInput {
  readonly name?: string
  readonly is_active?: boolean
  readonly gateways?: readonly string[]
  /** Sets ("HH:MM") or clears ("") the settlement cut-off (FR-41). */
  readonly settlement_day_start?: string
}

interface BillerMutationResult {
  readonly biller: Biller
  /** Set when the organisation was created but its first administrator could
   *  not be invited — a partial success the screen must surface, because the
   *  biller exists and someone has to be told to add the user by hand. */
  readonly warning?: string
}

export const createBiller = (body: CreateBillerInput, csrfToken: string): Promise<BillerMutationResult> =>
  unwrap(bffRequest<Envelope<BillerMutationResult>>(`${API}/v1/billers`, { method: 'POST', body, csrfToken }))

export const updateBiller = (
  code: string,
  body: UpdateBillerInput,
  csrfToken: string,
): Promise<BillerMutationResult> =>
  unwrap(
    bffRequest<Envelope<BillerMutationResult>>(`${API}/v1/billers/${encodeURIComponent(code)}`, {
      method: 'PATCH',
      body,
      csrfToken,
    }),
  )

export const setGatewayLabel = (id: string, label: string, csrfToken: string): Promise<Gateway> =>
  unwrap(
    bffRequest<Envelope<Gateway>>(`${API}/v1/gateways/${encodeURIComponent(id)}/label`, {
      method: 'PUT',
      body: { gateway_label: label },
      csrfToken,
    }),
  )

/** An empty `billerId` detaches the gateway from whoever holds it. */
export const assignGateway = (
  id: string,
  billerId: string,
  csrfToken: string,
): Promise<{ gateway_id: string; biller_id: string }> =>
  unwrap(
    bffRequest<Envelope<{ gateway_id: string; biller_id: string }>>(
      `${API}/v1/gateways/${encodeURIComponent(id)}/biller`,
      { method: 'PUT', body: { biller_id: billerId }, csrfToken },
    ),
  )

export const getUsers = (signal?: AbortSignal): Promise<UsersResponse> =>
  unwrap(bffRequest<Envelope<UsersResponse>>(`${API}/v1/users`, signal ? { signal } : {}))

export const getAudit = async (signal?: AbortSignal): Promise<readonly AuditEvent[]> =>
  (await unwrap(bffRequest<Envelope<{ events: AuditEvent[] }>>(`${API}/v1/audit`, signal ? { signal } : {}))).events

export interface CreateUserInput {
  readonly email: string
  readonly first_name: string
  readonly last_name: string
}

export interface UpdateUserInput {
  readonly first_name?: string
  readonly last_name?: string
  readonly enabled?: boolean
}

interface UserMutationResult {
  readonly user: PortalUser
  /** The API returns a warning when the account was changed but the
   *  notification e-mail could not be sent — a partial success the UI must
   *  show, because treating it as a plain success hides a user who will never
   *  receive their invitation. */
  readonly warning: string
}

export const createUser = (body: CreateUserInput, csrfToken: string): Promise<UserMutationResult> =>
  unwrap(bffRequest<Envelope<UserMutationResult>>(`${API}/v1/users`, { method: 'POST', body, csrfToken }))

export const updateUser = (id: string, body: UpdateUserInput, csrfToken: string): Promise<UserMutationResult> =>
  unwrap(
    bffRequest<Envelope<UserMutationResult>>(`${API}/v1/users/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body,
      csrfToken,
    }),
  )

export const resetUserPassword = (id: string, csrfToken: string): Promise<{ sent: boolean }> =>
  unwrap(
    bffRequest<Envelope<{ sent: boolean }>>(`${API}/v1/users/${encodeURIComponent(id)}/reset-password`, {
      method: 'POST',
      csrfToken,
    }),
  )

/**
 * The XLSX export. Not `bffRequest`, because the response is a binary stream
 * rather than an envelope — but the error path still is one, so a failure is
 * unwrapped into the same BffError every other call throws.
 *
 * Returns the blob and filename rather than triggering the download itself:
 * the DOM dance belongs to the component, and keeping it out of here is what
 * makes this testable and what makes the eventual move to an async job with a
 * download link a change at one call site.
 */
export async function exportTransactions(
  q: TransactionQuery,
  signal?: AbortSignal,
): Promise<{ readonly blob: Blob; readonly filename: string }> {
  const response = await fetch(`${API}/v1/transactions/export?${toQueryString(q)}`, {
    credentials: 'same-origin',
    ...(signal ? { signal } : {}),
  })
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as Envelope<unknown> | null
    throw new BffError(
      response.status,
      body?.error?.code ?? 'export_failed',
      body?.error?.message ?? `export failed with status ${response.status}`,
      body?.meta?.request_id ?? null,
    )
  }
  const disposition = response.headers.get('content-disposition') ?? ''
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'transactions.xlsx'
  return { blob: await response.blob(), filename }
}

/** POST /v1/support — multipart, because the request carries files. The
 *  identity of the sender is the session's; only topic, message and
 *  attachments travel in the form. */
export const sendSupportRequest = (
  input: SupportRequestInput,
  csrfToken: string,
): Promise<{ sent: boolean }> => {
  const form = new FormData()
  form.set('topic', input.topic)
  form.set('message', input.message)
  for (const file of input.attachments) form.append('attachments', file, file.name)
  return unwrap(
    bffRequest<Envelope<{ sent: boolean }>>(`${API}/v1/support`, {
      method: 'POST',
      body: form,
      csrfToken,
    }),
  )
}

export const getFieldExposure = (signal?: AbortSignal): Promise<FieldExposureResponse> =>
  unwrap(bffRequest<Envelope<FieldExposureResponse>>(`${API}/v1/field-exposure`, signal ? { signal } : {}))

export const grantFieldExposure = (
  body: GrantFieldExposureInput,
  csrfToken: string,
): Promise<FieldExposureRule> =>
  unwrap(
    bffRequest<Envelope<FieldExposureRule>>(`${API}/v1/field-exposure`, {
      method: 'POST',
      body,
      csrfToken,
    }),
  )

export const revokeFieldExposure = (id: string, csrfToken: string): Promise<{ revoked: string }> =>
  unwrap(
    bffRequest<Envelope<{ revoked: string }>>(
      `${API}/v1/field-exposure/${encodeURIComponent(id)}`,
      { method: 'DELETE', csrfToken },
    ),
  )

// ── beneficiaries (V1.1) ────────────────────────────────────────────────

export const getGatewayBeneficiaries = (id: string, signal?: AbortSignal): Promise<GatewayBeneficiaries> =>
  unwrap(bffRequest<Envelope<GatewayBeneficiaries>>(`${API}/v1/gateways/${encodeURIComponent(id)}/beneficiaries`, signal ? { signal } : {}))

/** The slices one organisation holds — the edit form's view (internal only). */
export const getBillerSlices = (code: string, signal?: AbortSignal): Promise<BillerSlices> =>
  unwrap(bffRequest<Envelope<BillerSlices>>(`${API}/v1/billers/${encodeURIComponent(code)}/beneficiaries`, signal ? { signal } : {}))

export interface SliceInput {
  readonly code: string
  readonly gatewayId: string
  readonly beneficiaryCode: string
}

/** Gives a beneficiary organisation one slice: its identity on a gateway. */
export const assignSlice = ({ code, gatewayId, beneficiaryCode }: SliceInput, csrfToken: string) =>
  unwrap(
    bffRequest<Envelope<{ biller_id: string; gateway_id: string; beneficiary_code: string }>>(
      `${API}/v1/billers/${encodeURIComponent(code)}/beneficiaries`,
      { method: 'POST', body: { gateway_id: gatewayId, beneficiary_code: beneficiaryCode }, csrfToken },
    ),
  )

export const revokeSlice = ({ code, gatewayId, beneficiaryCode }: SliceInput, csrfToken: string) =>
  unwrap(
    bffRequest<Envelope<{ revoked: boolean }>>(`${API}/v1/billers/${encodeURIComponent(code)}/beneficiaries/revoke`, {
      method: 'POST',
      body: { gateway_id: gatewayId, beneficiary_code: beneficiaryCode },
      csrfToken,
    }),
  )
